import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { Order } from '../../../api/queries/order.queries';
import { useUpdateOrderStatus } from '../../../api/mutations/kds.mutations';

interface OrderCardProps {
  order: Order;
  onNewOrder?: (order: Order) => void; // For audio announcement callback
}

// Sweetness level labels
const SWEETNESS_LABELS: Record<string, string> = {
  '0': 'No Sugar',
  '25': '25%',
  '50': '50%',
  '70': '70%',
  '100': '100%',
};

// Size labels
const SIZE_LABELS: Record<string, string> = {
  'small': 'S',
  'S': 'S',
  'medium': 'M',
  'M': 'M',
  'large': 'L',
  'L': 'L',
};

function OrderCard({ order, onNewOrder }: OrderCardProps) {
  const { t } = useTranslation();
  // Show items expanded by default for better visibility
  const [isExpanded, setIsExpanded] = useState(true);
  const [waitingMinutes, setWaitingMinutes] = useState(0);
  const updateStatusMutation = useUpdateOrderStatus(order._id);
  const announcedRef = useRef(false);

  // Calculate waiting time
  useEffect(() => {
    const updateTimer = () => {
      const now = new Date();
      const created = new Date(order.createdAt);
      const diffMs = now.getTime() - created.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      setWaitingMinutes(diffMins);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 10000); // Update every 10 seconds

    return () => clearInterval(interval);
  }, [order.createdAt]);

  // Announce new orders
  useEffect(() => {
    if (order.status === 'new' && !announcedRef.current && onNewOrder) {
      onNewOrder(order);
      announcedRef.current = true;
    }
  }, [order, onNewOrder]);

  const handleStatusChange = async (newStatus: 'in_progress' | 'ready' | 'completed') => {
    try {
      await updateStatusMutation.mutateAsync({ status: newStatus });
    } catch (error) {
      console.error('Failed to update order status:', error);
    }
  };

  // Determine next status action
  const getNextAction = () => {
    switch (order.status) {
      case 'new':
        return { label: t('kds.startPreparing'), status: 'in_progress' as const, color: 'bg-blue-500 hover:bg-blue-600' };
      case 'in_progress':
        return { label: t('kds.markReady'), status: 'ready' as const, color: 'bg-green-500 hover:bg-green-600' };
      case 'ready':
        return { label: t('kds.complete'), status: 'completed' as const, color: 'bg-gray-500 hover:bg-gray-600' };
      default:
        return null;
    }
  };

  const nextAction = getNextAction();

  // Color escalation based on waiting time (5 min yellow, 10 min red) - NO blinking
  const getUrgencyStyles = () => {
    if (waitingMinutes >= 10) {
      return {
        border: 'border-l-4 border-l-red-500',
        bg: 'bg-red-50 dark:bg-red-950/20',
      };
    }
    if (waitingMinutes >= 5) {
      return {
        border: 'border-l-4 border-l-yellow-500',
        bg: 'bg-yellow-50 dark:bg-yellow-950/20',
      };
    }
    return {
      border: 'border-l-4 border-l-green-500',
      bg: 'bg-card',
    };
  };

  const urgencyStyles = getUrgencyStyles();

  // Display number - prefer daily number, fallback to order number
  const displayNumber = order.dailyNumber || order.orderNumber;

  // Format time badge color
  const getTimeBadgeStyles = () => {
    if (waitingMinutes >= 10) return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300';
    if (waitingMinutes >= 5) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300';
    return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300';
  };

  return (
    <div className={`rounded-lg border shadow-sm overflow-hidden ${urgencyStyles.border} ${urgencyStyles.bg}`}>
      {/* Header with prominent daily number */}
      <div className="p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {/* Large daily number with proper padding */}
            <div className="flex items-center justify-center min-w-[56px] h-14 px-3 rounded-xl bg-primary text-primary-foreground">
              <span className="text-xl font-bold">#{displayNumber}</span>
            </div>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground font-mono truncate">
                {order.orderNumber}
              </p>
              <p className="text-sm text-muted-foreground">
                {order.items.length} {order.items.length === 1 ? 'item' : 'items'}
              </p>
            </div>
          </div>
          <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getTimeBadgeStyles()}`}>
            <Clock className="h-3 w-3" />
            <span>{waitingMinutes}m</span>
          </div>
        </div>

        {/* Expand/Collapse button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between text-sm text-muted-foreground hover:text-foreground mt-2 pt-2 border-t tap-highlight-none"
        >
          <span>{isExpanded ? t('common.collapse') || 'Hide Details' : t('common.expand') || 'Show Details'}</span>
          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {/* Order Items - visible by default */}
      {isExpanded && (
        <div className="px-3 pb-3">
          <div className="p-3 rounded-lg bg-background/80 space-y-2.5">
            {order.items.map((item, idx) => (
              <div key={idx} className="text-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-foreground">
                      {item.quantity}x {item.menuItemName}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                      <span className="px-1.5 py-0.5 rounded bg-secondary font-medium">
                        {SIZE_LABELS[item.size] || item.size}
                      </span>
                      <span>•</span>
                      <span>{SWEETNESS_LABELS[item.sweetness || item.sweetnessLevel?.toString() || '100'] || `${item.sweetnessLevel || 100}%`} sweet</span>
                    </div>
                    {/* Modifiers */}
                    {item.modifiers && item.modifiers.length > 0 && (
                      <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                        {item.modifiers.map((mod, midx) => (
                          <span key={midx} className="mr-2">+ {mod.name}</span>
                        ))}
                      </div>
                    )}
                    {/* Toppings */}
                    {item.toppings && item.toppings.length > 0 && (
                      <div className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                        {item.toppings.map((topping, tidx) => (
                          <span key={tidx} className="mr-2">+ {topping.name}</span>
                        ))}
                      </div>
                    )}
                    {/* Special notes */}
                    {item.notes && (
                      <div className="text-xs italic text-orange-600 dark:text-orange-400 mt-1.5 border-l-2 border-orange-500 pl-2 py-0.5 bg-orange-50 dark:bg-orange-950/30 rounded-r">
                        {item.notes}
                      </div>
                    )}
                  </div>
                </div>
                {idx < order.items.length - 1 && <div className="border-b border-dashed mt-2.5" />}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Button */}
      {nextAction && (
        <div className="p-3 pt-0">
          <button
            onClick={() => handleStatusChange(nextAction.status)}
            disabled={updateStatusMutation.isPending}
            className={`w-full py-3 rounded-lg font-bold text-white transition-all touch-target tap-highlight-none disabled:opacity-50 ${nextAction.color} active:scale-[0.98]`}
          >
            {updateStatusMutation.isPending ? t('common.loading') : nextAction.label}
          </button>
        </div>
      )}
    </div>
  );
}

export default OrderCard;
